interface IContrastPicker {
  contrastRatio(foregroundHex: string, backgroundHex: string): number;
  pick(backgroundHex: string, candidateHexes: readonly string[]): string;
}

/** WCAG 2.x contrast constant — `(L1 + 0.05) / (L2 + 0.05)`. */
const LUMINANCE_OFFSET = 0.05;

/**
 * WCAG relative-luminance contrast for foregrounds sitting on a resolved fill. `SemanticMapper` uses it to
 * choose white or black per `on-*` token rather than hard-coding one — a `vivid` brand fill is light enough
 * that white on it is unreadable. WCAG rather than OKLCH lightness because the 4.5:1 threshold is defined in
 * WCAG terms and OKLCH `l` is not a monotonic proxy for it across hues.
 */
export const ContrastPicker: IContrastPicker = {
  /**
   * Compute the WCAG contrast ratio between two colours. Order-independent.
   * @param foregroundHex Foreground hex (`#rrggbb`, `#rgb`, `#` optional).
   * @param backgroundHex Background hex, same accepted forms.
   * @returns Ratio in `1..21`; `1` when either colour cannot be parsed.
   */
  contrastRatio(foregroundHex: string, backgroundHex: string): number {
    const fg = _relativeLuminance(foregroundHex);
    const bg = _relativeLuminance(backgroundHex);
    if (fg === null || bg === null) return 1;
    const lighter = Math.max(fg, bg);
    const darker = Math.min(fg, bg);
    return (lighter + LUMINANCE_OFFSET) / (darker + LUMINANCE_OFFSET);
  },
  /**
   * Choose the candidate colour with the highest contrast against a background.
   * Ties — and unparseable backgrounds — resolve to the first candidate, so callers get a
   * deterministic result for non-hex literals like `transparent`.
   * @param backgroundHex The fill the foreground will sit on.
   * @param candidateHexes Candidate foregrounds, most-preferred first. Must not be empty.
   * @returns The winning candidate, returned verbatim from `candidateHexes`.
   */
  pick(backgroundHex: string, candidateHexes: readonly string[]): string {
    if (candidateHexes.length === 0) throw new Error('ContrastPicker: candidateHexes must not be empty');
    let best = candidateHexes[0];
    let bestRatio = this.contrastRatio(best, backgroundHex);
    for (const candidate of candidateHexes.slice(1)) {
      const ratio = this.contrastRatio(candidate, backgroundHex);
      if (ratio > bestRatio) {
        best = candidate;
        bestRatio = ratio;
      }
    }
    return best;
  }
};

const _channelLuminance = (channel: number): number =>
  channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

const _relativeLuminance = (hex: string): number | null => {
  const rgb = _toRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(_channelLuminance);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const _toRgb = (hex: string): [number, number, number] | null => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const body = match[1];
  const expanded =
    body.length === 3
      ? body
          .split('')
          .map(ch => ch + ch)
          .join('')
      : body;
  return [
    Number.parseInt(expanded.slice(0, 2), 16) / 255,
    Number.parseInt(expanded.slice(2, 4), 16) / 255,
    Number.parseInt(expanded.slice(4, 6), 16) / 255
  ];
};
